import { DatePipe } from '@angular/common';
import { QueryParams } from '../app/types/query-params';
import { generateProductCodesUri, generateUriParams } from './buildRequest';
import { getDateLowerBound, getDateTime } from './dateHelper';
import {
  doRequest,
  getDateValue,
  getWorksheetFromRange,
  setDateValues,
  setNumberFormat,
} from './util';

/* global Excel, window, console, Promise, CustomFunctions */

const pipe = new DatePipe('en-US');

export async function getPrices(
  product_codes: string[][],
  currency: string,
  unit: string,
  resolution: string,
  aggregation: string,
  aggregation_scope: string,
  aggregation_method: string,
  start_date: number | string,
  end_date: number | string,
  last_price_only: boolean,
  output_format: string,
  auto_update: boolean,
  nv_default: string,
  time_zone: string,
  hpfc: boolean,
  start_of_day: number | null,
  invocation: CustomFunctions.Invocation
) {
  let startDate = getDateLowerBound(getDateTime(start_date), aggregation);
  let endDate = getDateTime(end_date);
  let context = new Excel.RequestContext();
  context.workbook.worksheets
    .getItem(getWorksheetFromRange(invocation.address!))
    .getRange(invocation.address!.split('!')[1])
    .getColumn(0).format.columnWidth = 85;
  await context.sync();
  try {
    let url = window.api_url + '/prices/historic_data?';
    let products;
    let url_params = '';
    let url_product_codes = '';
    if (product_codes === undefined || product_codes.length === 0) {
      return [['EMSA ids must be given']];
    } else {
      products = ([] as any[]).concat.apply([], product_codes);
      if ('SKIP' === nv_default.toUpperCase() && products.length > 1) {
        return [
          [
            'Error in addin: Can not request more than 1 product_code when skip option is used as nv_default',
          ],
        ];
      }
      if (startDate && endDate) {
        if (startDate > endDate) {
          return [['end_date must be after start_date']];
        }
      }
      url_product_codes = generateProductCodesUri(products);
      url_params = generateUriParams(
        new QueryParams(
          resolution,
          aggregation,
          aggregation_scope,
          aggregation_method,
          nv_default,
          unit,
          currency,
          time_zone,
          last_price_only,
          auto_update,
          '',
          start_of_day ?? undefined
        ),
        startDate,
        endDate,
        hpfc,
        false
      );
      if (output_format === '') {
        output_format = 'default';
      }
    }
    const urls: any[] = [];
    while (url_product_codes.length + url_params.length >= 2000) {
      if (startDate === null) {
        return [
          [
            'Error: Can not query all for this amount of product_codes, specify a query_start and try again',
          ],
        ];
      }
      let url = url_product_codes.substring(0, 1500);
      let last_index = url.lastIndexOf('&');
      url = url.substring(0, last_index);
      urls.push(window.api_url + '/prices/historic_data?' + url + url_params);
      url_product_codes = url_product_codes.substring(last_index + 1);
    }
    urls.push(
      window.api_url + '/prices/historic_data?' + url_product_codes + url_params
    );
    const results: any[] = [];
    for (const url of urls) {
      results.push(await doRequest(url));
    }
    let json_objects: any[] = [];
    for (const result of results) {
      if (!result.ok) {
        let error = await result.json();
        return [[`${result.statusText}: ${error.message} ${url}`]];
      }
      let json = await result.json();
      json_objects.push(json);
    }
    let json_response = json_objects[0];
    for (let i = 1; i < json_objects.length; i++) {
      if (json_objects[i] !== undefined) {
        json_objects[i].time_series.forEach((time_series: any) => {
          json_response.time_series.push(time_series);
        });
      }
    }
    json_response.time_series.sort(
      (
        a: {
          details: {
            delivery_start: string | number | Date | null | undefined;
            product_code: number;
          };
        },
        b: {
          details: {
            delivery_start: string | number | Date | null | undefined;
            product_code: number;
          };
        }
      ) => {
        if (
          a.details.delivery_start !== undefined &&
          b.details.delivery_start !== undefined &&
          a.details.delivery_start !== null &&
          b.details.delivery_start !== null
        ) {
          let dateA = new Date(a.details.delivery_start);
          let dateB = new Date(b.details.delivery_start);
          if (dateA.getTime() - dateB.getTime() === 0) {
            return a.details.product_code > b.details.product_code ? 1 : -1;
          }
          return dateA.getTime() - dateB.getTime();
        } else {
          return a.details.product_code > b.details.product_code ? 1 : -1;
        }
      }
    );
    if (json_response.time_series.length > 0) {
      const allTimezonesEqual = json_response.time_series.every(
        (time_series) =>
          time_series.details.time_zone ===
          json_response.time_series[0].details.time_zone
      );
      if (
        !allTimezonesEqual &&
        (time_zone === undefined || time_zone === null || time_zone === '')
      ) {
        return [
          [
            'If you request product_codes with different timezones, you have to specify the timezone parameter',
          ],
        ];
      }
      let numberFormat = resolution.includes('month')
        ? 'mm.yyyy'
        : resolution.includes('day')
          ? 'dd.mm.yyyy'
          : resolution.includes('hour') || resolution.includes('min')
            ? 'dd.mm.yyyy hh:MM'
            : json_response.time_series[0].details.resolution.includes('month')
              ? 'mm.yyyy'
              : json_response.time_series[0].details.resolution.includes('day')
                ? 'dd.mm.yyyy'
                : 'dd.mm.yyyy hh:MM';
      if (last_price_only && !hpfc) {
        numberFormat = 'dd.mm.yyyy';
        try {
          const context = new Excel.RequestContext();
          const range = context.workbook.worksheets
            .getItem(getWorksheetFromRange(invocation.address!))
            .getRange(invocation.address!.split('!')[1])
            .getResizedRange(2, 1)
            .getLastCell()
            .getResizedRange(1, json_response.time_series.length - 1);
          range.numberFormat = [[numberFormat]];
          await context.sync();
          let ret: any[] = [[''], [''], [''], ['Date'], ['Value']];
          for (let i = 0; i < json_response.time_series.length; i++) {
            let time_series = json_response.time_series[i];
            ret[0].push(time_series.details.product_code);
            ret[1].push(time_series.details.description);
            ret[2].push(
              time_series.details.delivery_start === null
                ? 'no delivery start'
                : await getDateValue(time_series.details.delivery_start)
            );
            if (time_series.values.length > 0) {
              ret[3].push(
                time_series.values[0][0] === null
                  ? ''
                  : await getDateValue(time_series.values[0][0])
              );
              ret[4].push(
                time_series.values[0][time_series.values[0].length - 1]
              );
            } else {
              ret[3].push('no last date');
              ret[4].push('no value');
            }
          }
          if (auto_update) {
            return new Promise<number[][]>((resolve) => {
              resolve(ret);
            });
          } else {
            let context = new Excel.RequestContext();
            let range = context.workbook.worksheets
              .getItem(getWorksheetFromRange(invocation.address!))
              .getRange(invocation.address!.split('!')[1])
              .getResizedRange(4, ret[0].length - 1);
            range.values = ret;
            await context.sync();
            return;
          }
        } catch (error: any) {
          return [[error.message]];
        }
      } else {
        if (json_response.time_series[0].values.length > 0) {
          let prices: any[][];
          if (json_response.time_series[0].values[0].length === 3) {
            let values_length = json_response.time_series[0].values.length;
            for (let i = 0; i < values_length; i++) {
              json_response.time_series[0].values[i].splice(1, 1);
            }
          }
          prices = json_response.time_series[0].values;
          let header_arr: any[] = [];
          header_arr[0] = [];
          header_arr[1] = [];
          header_arr[2] = [];
          header_arr[3] = [];
          header_arr[4] = [];
          header_arr[5] = [];
          if (auto_update) {
            prices = setDateValues(prices);
          } else {
            prices.forEach(function (row: string[]) {
              row[0] = `=VALUE("${row[0].replace('T', ' ').split('+')[0]}")`;
            });
          }
          await setNumberFormat(
            numberFormat,
            prices.length - 1,
            json_response.time_series.length,
            output_format === 'diagram' ? 1 : header_arr.length,
            0,
            invocation.address
          );
          let time_series_count = json_response.time_series.length;
          let nv_value;
          if (nv_default === '') {
            nv_value = '';
          } else if (nv_default === 'na') {
            nv_value = 'N/A';
          } else if (nv_default === 'nv') {
            nv_value = auto_update
              ? new CustomFunctions.Error(
                  CustomFunctions.ErrorCode.notAvailable
                )
              : '=NA()';
          }
          let is_calc_date = false;
          for (let i = 0; i < time_series_count; i++) {
            let values = json_response.time_series[i].values;
            let values_length = values.length;
            for (let j = 0; j < values_length; j++) {
              let value = values[j][values[j].length - 1];
              if (value === null) {
                value = nv_value;
              }
              prices[j][i + 1] = value;
            }
            let details = json_response.time_series[i].details;
            is_calc_date =
              details.date_of_value === undefined ||
              details.date_of_value === null;
            let calc_del_date = hpfc
              ? details.date_of_value !== undefined &&
                details.date_of_value !== null
                ? pipe.transform(new Date(details.date_of_value), 'dd.MM.yyyy')
                : details.updated !== null
                  ? pipe.transform(new Date(details.updated), 'dd.MM.yyyy')
                  : ''
              : details.delivery_start !== null
                ? pipe.transform(new Date(details.delivery_start), 'dd.MM.yyyy')
                : '';
            header_arr[0].push(details.product_code);
            header_arr[1].push(details.description);
            header_arr[2].push(calc_del_date);
            header_arr[3].push(
              details.returned_unit !== null &&
                details.returned_unit !== undefined
                ? details.returned_unit
                : details.unit
            );
            header_arr[4].push(
              details.returned_currency !== null &&
                details.returned_currency !== undefined
                ? details.returned_currency
                : details.currency
            );
            header_arr[5].push(
              time_zone !== undefined && time_zone !== null && time_zone !== ''
                ? time_zone
                : details.time_zone !== null
                  ? details.time_zone
                  : ''
            );
          }
          header_arr[0].unshift(
            output_format === 'default' ? 'Product code' : ''
          );
          header_arr[1].unshift(
            output_format === 'default' ? 'Description' : ''
          );
          header_arr[2].unshift(
            hpfc && !is_calc_date
              ? 'Date of Value'
              : hpfc
                ? 'Calculation date'
                : 'First delivery date'
          );
          header_arr[3].unshift(output_format === 'default' ? 'Unit' : '');
          header_arr[4].unshift(output_format === 'default' ? 'Currency' : '');
          header_arr[5].unshift(output_format === 'default' ? 'Timezone' : '');
          if (output_format === '' || output_format === 'default') {
            prices.unshift(header_arr[5]);
            prices.unshift(header_arr[4]);
            prices.unshift(header_arr[3]);
            prices.unshift(header_arr[2]);
            prices.unshift(header_arr[1]);
            prices.unshift(header_arr[0]);
          } else if (output_format === 'diagram') {
            prices.unshift(header_arr[0]);
          }
          prices = normalizePrices(prices);
          if (auto_update) {
            return new Promise<number[][]>((resolve) => {
              resolve(prices);
            });
          } else {
            let context = new Excel.RequestContext();
            let range = context.workbook.worksheets
              .getItem(getWorksheetFromRange(invocation.address!))
              .getRange(invocation.address!.split('!')[1])
              .getResizedRange(prices.length - 1, product_codes.length);
            range.values = prices;
            range.copyFrom(range, Excel.RangeCopyType.values);
            await context.sync();
            return;
          }
        } else {
          return [['no values for given query period']];
        }
      }
    } else {
      return [['no values for given product code(s)']];
    }
  } catch (error: any) {
    console.error(error);
    return [['error occured in addin: ' + error.message]];
  }
}

function normalizePrices(prices: any[][]) {
  const len1 = prices.length;
  const len2 = prices[0].length;
  for (let i = 0; i < len1; i++) {
    for (let j = 0; j < len2; j++) {
      if (prices[i][j] === undefined) {
        prices[i][j] = '';
      }
    }
  }
  return prices;
}

export async function getVersionedPrices(
  product_codes: string[][],
  currency: string,
  unit: string,
  resolution: string,
  aggregation: string,
  aggregation_scope: string,
  aggregation_method: string,
  start_date: number | string,
  end_date: number | string,
  last_price_only: boolean,
  output_format: string,
  auto_update: boolean,
  nv_default: string,
  time_zone: string,
  hpfc: boolean,
  max_version: string | number,
  start_of_day: number | null,
  invocation: CustomFunctions.Invocation
) {
  let startDate = getDateLowerBound(getDateTime(start_date), aggregation);
  let endDate = getDateTime(end_date);
  let context = new Excel.RequestContext();
  context.workbook.worksheets
    .getItem(getWorksheetFromRange(invocation.address!))
    .getRange(invocation.address!.split('!')[1])
    .getColumn(0).format.columnWidth = 85;
  await context.sync();
  try {
    let url = window.api_url + '/prices/historic_data?';
    let products;
    let url_params = '';
    let url_product_codes = '';
    if (product_codes === undefined || product_codes.length === 0) {
      return [['EMSA ids must be given']];
    } else {
      products = ([] as any[]).concat.apply([], product_codes);
      if ('SKIP' === nv_default.toUpperCase() && products.length > 1) {
        return [
          [
            'Error in addin: Can not request more than 1 product_code when skip option is used as nv_default',
          ],
        ];
      }
      if (startDate && endDate) {
        if (startDate > endDate) {
          return [['end_date must be after start_date']];
        }
      }
      url_product_codes = generateProductCodesUri(products);
      url_params = generateUriParams(
        new QueryParams(
          resolution,
          aggregation,
          aggregation_scope,
          aggregation_method,
          nv_default,
          unit,
          currency,
          time_zone,
          last_price_only,
          auto_update,
          max_version,
          start_of_day ?? undefined
        ),
        startDate,
        endDate,
        hpfc,
        false
      );
      if (output_format === '') {
        output_format = 'default';
      }
    }
    const urls: any[] = [];
    while (url_product_codes.length + url_params.length >= 2000) {
      if (!startDate) {
        return [
          [
            'Error: Can not query all for this amount of product_codes, specify a query_start and try again',
          ],
        ];
      }
      let url = url_product_codes.substring(0, 1500);
      let last_index = url.lastIndexOf('&');
      url = url.substring(0, last_index);
      urls.push(window.api_url + '/prices/historic_data?' + url + url_params);
      url_product_codes = url_product_codes.substring(last_index + 1);
    }
    urls.push(
      window.api_url + '/prices/historic_data?' + url_product_codes + url_params
    );
    const results: any[] = [];
    for (let i = 0; i < urls.length; i++) {
      let res = await doRequest(urls[i]);
      results.push(res);
    }
    let json_objects: any[] = [];
    for (let i = 0; i < results.length; i++) {
      if (!results[i].ok) {
        let error = await results[i].json();
        return [[results[i].statusText + ':' + error.message + '  ' + url]];
      }
      let json = await results[i].json();
      json_objects.push(json);
    }
    let json_response = json_objects[0];
    for (let i = 1; i < json_objects.length; i++) {
      if (json_objects[i] !== undefined) {
        json_objects[i].time_series.forEach((time_series: any) => {
          json_response.time_series.push(time_series);
        });
      }
    }
    json_response.time_series.sort(
      (
        a: {
          details: {
            delivery_start: string | number | Date | null | undefined;
            product_code: number;
          };
        },
        b: {
          details: {
            delivery_start: string | number | Date | null | undefined;
            product_code: number;
          };
        }
      ) => {
        if (
          a.details.delivery_start !== undefined &&
          b.details.delivery_start !== undefined &&
          a.details.delivery_start !== null &&
          b.details.delivery_start !== null
        ) {
          let dateA = new Date(a.details.delivery_start);
          let dateB = new Date(b.details.delivery_start);
          if (dateA.getTime() - dateB.getTime() === 0) {
            return a.details.product_code > b.details.product_code ? 1 : -1;
          }
          return dateA.getTime() - dateB.getTime();
        } else {
          return a.details.product_code > b.details.product_code ? 1 : -1;
        }
      }
    );
    if (json_response.time_series.length > 0) {
      const allTimezonesEqual = json_response.time_series.every(
        (time_series) =>
          time_series.details.time_zone ===
          json_response.time_series[0].details.time_zone
      );
      if (
        !allTimezonesEqual &&
        (time_zone === undefined || time_zone === null || time_zone === '')
      ) {
        return [
          [
            'If you request product_codes with different timezones, you have to specify the timezone parameter',
          ],
        ];
      }
      let numberFormat = resolution.includes('month')
        ? 'mm.yyyy'
        : resolution.includes('day')
          ? 'dd.mm.yyyy'
          : resolution.includes('hour') || resolution.includes('min')
            ? 'dd.mm.yyyy hh:MM'
            : json_response.time_series[0].details.resolution.includes('month')
              ? 'mm.yyyy'
              : json_response.time_series[0].details.resolution.includes('day')
                ? 'dd.mm.yyyy'
                : 'dd.mm.yyyy hh:MM';
      if (last_price_only && !hpfc) {
        numberFormat = 'dd.mm.yyyy';
        try {
          const context = new Excel.RequestContext();
          const range = context.workbook.worksheets
            .getItem(getWorksheetFromRange(invocation.address!))
            .getRange(invocation.address!.split('!')[1])
            .getResizedRange(2, 1)
            .getLastCell()
            .getResizedRange(1, json_response.time_series.length - 1);
          range.numberFormat = [[numberFormat]];
          await context.sync();
          let ret: any[] = [[''], [''], [''], ['Date'], ['Value']];
          for (let i = 0; i < json_response.time_series.length; i++) {
            let time_series = json_response.time_series[i];
            ret[0].push(time_series.details.product_code);
            ret[1].push(time_series.details.description);
            ret[2].push(
              time_series.details.delivery_start === null
                ? 'no delivery start'
                : await getDateValue(time_series.details.delivery_start)
            );
            if (time_series.values.length > 0) {
              ret[3].push(
                time_series.values[0][0] === null
                  ? ''
                  : await getDateValue(time_series.values[0][0])
              );
              ret[4].push(
                time_series.values[0][time_series.values[0].length - 1]
              );
            } else {
              ret[3].push('no last date');
              ret[4].push('no value');
            }
          }
          if (auto_update) {
            return new Promise<number[][]>((resolve) => {
              resolve(ret);
            });
          } else {
            let context = new Excel.RequestContext();
            let range = context.workbook.worksheets
              .getItem(getWorksheetFromRange(invocation.address!))
              .getRange(invocation.address!.split('!')[1])
              .getResizedRange(4, ret[0].length - 1);
            range.values = ret;
            await context.sync();
            return;
          }
        } catch (error: any) {
          return [[error.message]];
        }
      } else {
        if (json_response.time_series[0].values.length > 0) {
          let prices: any[][];
          if (json_response.time_series[0].values[0].length === 3) {
            let values_length = json_response.time_series[0].values.length;
            for (let i = 0; i < values_length; i++) {
              json_response.time_series[0].values[i].splice(1, 1);
            }
          }
          prices = json_response.time_series[0].values;
          let header_arr: any[] = [];
          header_arr[0] = [];
          header_arr[1] = [];
          header_arr[2] = [];
          header_arr[3] = [];
          header_arr[4] = [];
          header_arr[5] = [];
          if (auto_update) {
            prices = setDateValues(prices);
          } else {
            prices.forEach(function (row: string[]) {
              row[0] = `=VALUE("${row[0].replace('T', ' ').split('+')[0]}")`;
            });
          }
          await setNumberFormat(
            numberFormat,
            prices.length - 1,
            json_response.time_series.length,
            output_format === 'diagram' ? 1 : header_arr.length,
            0,
            invocation.address
          );
          let time_series_count = json_response.time_series.length;
          let nv_value;
          if (nv_default === '') {
            nv_value = '';
          } else if (nv_default === 'na') {
            nv_value = 'N/A';
          } else if (nv_default === 'nv') {
            nv_value = auto_update
              ? new CustomFunctions.Error(
                  CustomFunctions.ErrorCode.notAvailable
                )
              : '=NA()';
          }
          let is_calc_date = false;
          for (let i = 0; i < time_series_count; i++) {
            let values = json_response.time_series[i].values;
            let values_length = values.length;
            for (let j = 0; j < values_length; j++) {
              let value = values[j][values[j].length - 1];
              if (value === null) {
                value = nv_value;
              }
              prices[j][i + 1] = value;
            }
            let details = json_response.time_series[i].details;
            is_calc_date =
              details.date_of_value === undefined ||
              details.date_of_value === null;
            let calc_del_date = hpfc
              ? details.date_of_value !== undefined &&
                details.date_of_value !== null
                ? pipe.transform(new Date(details.date_of_value), 'dd.MM.yyyy')
                : details.updated !== null
                  ? pipe.transform(new Date(details.updated), 'dd.MM.yyyy')
                  : ''
              : details.delivery_start !== null
                ? pipe.transform(new Date(details.delivery_start), 'dd.MM.yyyy')
                : '';
            header_arr[0].push(details.product_code);
            header_arr[1].push(details.description);
            header_arr[2].push(calc_del_date);
            header_arr[3].push(
              details.returned_unit !== null &&
                details.returned_unit !== undefined
                ? details.returned_unit
                : details.unit
            );
            header_arr[4].push(
              details.returned_currency !== null &&
                details.returned_currency !== undefined
                ? details.returned_currency
                : details.currency
            );
            header_arr[5].push(
              time_zone !== undefined && time_zone !== null && time_zone !== ''
                ? time_zone
                : details.time_zone !== null
                  ? details.time_zone
                  : ''
            );
          }
          header_arr[0].unshift(
            output_format === 'default' ? 'Product code' : ''
          );
          header_arr[1].unshift(
            output_format === 'default' ? 'Description' : ''
          );
          header_arr[2].unshift(
            hpfc && !is_calc_date
              ? 'Date of Value'
              : hpfc
                ? 'Calculation date'
                : 'First delivery date'
          );
          header_arr[3].unshift(output_format === 'default' ? 'Unit' : '');
          header_arr[4].unshift(output_format === 'default' ? 'Currency' : '');
          header_arr[5].unshift(output_format === 'default' ? 'Timezone' : '');
          if (output_format === '' || output_format === 'default') {
            prices.unshift(header_arr[5]);
            prices.unshift(header_arr[4]);
            prices.unshift(header_arr[3]);
            prices.unshift(header_arr[2]);
            prices.unshift(header_arr[1]);
            prices.unshift(header_arr[0]);
          } else if (output_format === 'diagram') {
            prices.unshift(header_arr[0]);
          }
          prices = normalizePrices(prices);
          if (auto_update) {
            return new Promise<number[][]>((resolve) => {
              resolve(prices);
            });
          } else {
            let context = new Excel.RequestContext();
            let range = context.workbook.worksheets
              .getItem(getWorksheetFromRange(invocation.address!))
              .getRange(invocation.address!.split('!')[1])
              .getResizedRange(prices.length - 1, product_codes.length);
            range.values = prices;
            range.copyFrom(range, Excel.RangeCopyType.values);
            await context.sync();
            return;
          }
        } else {
          return [['no values for given query period']];
        }
      }
    } else {
      return [['no values for given product code(s)']];
    }
  } catch (error: any) {
    console.error(error);
    return [['error occured in addin: ' + error.message]];
  }
}
